import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { toast } from "react-toastify";
import ImageWithFallback from "@components/ui/ImageFallback";
import RecreateButton from "./RecreateButton";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import WeeklyMealPlanner from "./WeeklyMealPlaner";
import { useNavigate } from "react-router-dom";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import { Loader2 } from "lucide-react";
import MuiDatePickerInput from "./CalenderComponent";
import { useTranslation } from "react-i18next";
import { downloadDayShoppingList, downloadShoppingList } from "api/dietPlan";
import { format } from "date-fns";

const AllMeals = ({
  recreatePlan,
  isMealPlanLoading,
  planPending,
  recipes,
  handleSetDietPlan,
  weeklyMeals,
  genratePlanAllowed,
  fetchDietPlan,
  selectedDay,
  setSelectedDay,
  changeInDay,
  dietPdfUrl,
  pdfGenerating,
  startDate,
  initialSelectedMeal,
  tab,
  date,
  fetchingDietPlan
}) => {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mondtlyList, setMonthlyList] = useState(false);
  const [dayList, setDayList] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [imageToShow, setImageToShow] = useState(
    weeklyMeals[0]?.image?.breakfast || ""
  );
  const [snacksImageHover, setSnacksImageHover] = useState(false);
  const [isSwiping, setIsSwiping] = useState(false);

  const [hoveredMeal, setHoveredMeal] = useState(null);
  const [slideImages, setSlideImages] = useState({});
  const isClickInProgress = useRef(false);
  const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dates = useMemo(() => {
    if (!startDate) return [];

    // Normalize to a Date object
    const base = new Date(startDate);
    if (isNaN(base.getTime())) return []; // invalid date guard

    return Array.from({ length: 7 }, (_, i) => {
      // copy base so we don't mutate the same Date instance
      const currentDate = new Date(base.getTime());
      currentDate.setDate(base.getDate() + i);

      const yyyyMmDd = currentDate.toISOString().split("T")[0];

      return {
        day: currentDate.toLocaleDateString("en-US", { weekday: "short" }),
        date: currentDate.getDate().toString(),
        fullDate: yyyyMmDd,
        title: currentDate.toDateString(),
        dateObj: currentDate,
      };
    });
  }, [startDate]);

  const mealLabels = ["Breakfast", "Lunch", "Dinner"];

  const handleDateClick = (index) => {
    setActiveIndex(index);
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideTo(index);
    }
  };

  const handleSliderChange = (swiper) => {
    const newRealIndex = swiper.realIndex;
    setActiveIndex(newRealIndex);

    // FIX 2: Ensure all hover state is cleared when the slide changes
    setHoveredMeal(null);
    setSnacksImageHover(false);

    // Update image immediately when slide changes
    if (weeklyMeals[newRealIndex]) {
      setSlideImages((prev) => ({
        ...prev,
        [newRealIndex]: weeklyMeals[newRealIndex].image.breakfast,
      }));
      // This sets the base image for the new slide/day
      setImageToShow(weeklyMeals[newRealIndex].image.breakfast);
    }
  };

  const handleDownloadShoppingList = async () => {
    try {
      setMonthlyList(true);
      const response = await downloadShoppingList();

      const fileBlob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(fileBlob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${t("Weekly_Grocery_List")}.pdf`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.info(t("toast.Weekly Grocery List downloaded successfully"));
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error(t("toast.Failed to download the file. Please try again"));
    } finally {
      setMonthlyList(false);
    }
  };

  const handleDownloadDay = async () => {
    try {
      setDayList(true);
      const day = currentRecipe?.title;
      const response = await downloadDayShoppingList(day);

      const fileBlob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(fileBlob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${t(`days.${day.toLowerCase()}`)}_${t("Grocery_List")}.pdf`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.info(t("toast.Daily Grocery List downloaded successfully"));
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error(t("toast.Failed to download the file. Please try again"));
    } finally {
      setDayList(false);
    }
  };

  const currentRecipe = recipes[activeIndex];

  const handleOpenRecipe = (label) => {
    isClickInProgress.current = true;
    const Key = label.toLowerCase();
    const newDate = new Date(date);
    navigate("/diet-plan/recipe-detail", {
      state: {
        mealType: Key,
        index:
          initialSelectedMeal.selectedMeals?.[
            weeklyMeals[activeIndex]?.title
          ]?.[Key] ?? 0,
        day: weeklyMeals[activeIndex]?.title,
        allMeals: true,
        tab: tab.type,
        tabCategory: tab?.category,
        startDate,
        dietPlanDate: newDate,
      },
    });
    setTimeout(() => {
      isClickInProgress.current = false;
    }, 0);
  };

  const handleViewDetails = (day) => {
    const newDate = new Date(date);
    navigate("/diet-plan/weekly-meal-planner", {
      state: {
        meals: weeklyMeals,
        daySelected: day,
        dietPlanDate: newDate,
        day:day
      },
    });
  };

  useEffect(() => {
    if (weeklyMeals?.length > 0) {
      const initialImages = {};
      weeklyMeals.forEach((meal, index) => {
        initialImages[index] = meal.image.breakfast;
      });
      setSlideImages(initialImages);
    }
  }, [weeklyMeals]);

  useEffect(() => {
    // Only proceed if not actively swiping
    if (!isSwiping) {
      if (!snacksImageHover) {
        if (hoveredMeal && weeklyMeals[activeIndex]) {
          setImageToShow(
            weeklyMeals[activeIndex].image[hoveredMeal.toLowerCase()]
          );
        } else if (weeklyMeals[activeIndex]) {
          setImageToShow(
            slideImages[activeIndex] || weeklyMeals[activeIndex].image.breakfast
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeIndex,
    hoveredMeal,
    slideImages,
    weeklyMeals,
    snacksImageHover,
    isSwiping,
  ]);

  useEffect(() => {
    if (selectedDay && recipes?.length > 0) {
      const selectedIndex = recipes.findIndex(
        (recipe) => recipe?.title === selectedDay
      );
      if (selectedIndex >= 0 && selectedIndex !== activeIndex) {
        setActiveIndex(selectedIndex);
        if (swiperRef.current && swiperRef.current.swiper) {
          swiperRef.current.swiper.slideTo(selectedIndex);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeInDay, startDate]);

  const getImageUrl = useCallback(
    (imagePath) => {
      return `${imagePath}`;
    },
    [BASE_URL]
  );

  const handleMouseEnter = (mealLabel) => {
    if (!isSwiping) {
      setHoveredMeal(mealLabel);
    }
  };

  const handleSnackMouseEnter = (snackType) => {
    if (!isSwiping) {
      setSnacksImageHover(true);
      setHoveredMeal(snackType);
      setImageToShow(weeklyMeals[activeIndex].image[snackType][0]);
    }
  };

  const handleMouseLeave = () => {
    if (!isClickInProgress.current) {
      setHoveredMeal(null);
    }
  };

  const handleSnackMouseLeave = () => {
    if (!isClickInProgress.current) {
      setHoveredMeal(null);
      setSnacksImageHover(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-end my-2">
        <MuiDatePickerInput fetchDietPlan={fetchDietPlan} dates={dates} isLoading={fetchingDietPlan} />
      </div>
      <div className="flex justify-between items-center mb-7">
        <h1 className="font-[Lato] text-[16px] font-[600] text-black">
          {t("dietPlanDashboard.allMeals.recipeHeading")}
        </h1>
        <RecreateButton
          recreatePlan={recreatePlan}
          isMealPlanLoading={isMealPlanLoading}
          planPending={planPending}
          t={t}
        />
      </div>

      {/* Dynamic Date Tabs */}
      <div className="flex items-center rounded-[8px] gap-2 w-full border border-[2px] border-[#F2F2F2B8] overflow-y-auto">
        {dates?.map((item, index) => (
          <div
            key={index}
            className={`flex flex-col flex-1 rounded-md !py-[28px] !px-[10px] cursor-pointer border border-white gap-[10px] ${
              activeIndex === index
                ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white "
                : "hover:border hover:border-[#0575E6] hover:text-[#0575E6]"
            }`}
            onClick={() => handleDateClick(index)}
          >
            <p
              className={`text-[14px] font-[Lato] ${
                activeIndex === index ? "text-white" : "text-[#767676]"
              }  font-[400] text-center`}
            >
              {/* {item?.day} */}
              {t(`dietPlanDashboard.allMeals.daysShort.${item?.day}`)}
            </p>
            <p className="text-[14px] font-[Lato] text-center">
              {format(item?.fullDate, "dd MMM")}
            </p>
          </div>
        ))}
      </div>

      {/* Recipe Swiper */}
      <div className="relative">
        <Swiper
          ref={swiperRef}
          modules={[Navigation]}
          spaceBetween={20}
          slidesPerView={1}
          // loop={true}
          onSlideChangeTransitionStart={() => {
            setIsSwiping(true);
            // Redundant, but harmless check: setHoveredMeal(null); setSnacksImageHover(false);
          }}
          onInit={() =>
            setTimeout(() => {
              setIsSwiping(false);
            }, 1000)
          }
          onSlideChangeTransitionEnd={() => setIsSwiping(false)}
          onTouchStart={() => setIsSwiping(true)}
          onTouchEnd={() => setIsSwiping(false)}
          navigation={{
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          }}
          onSlideChange={handleSliderChange}
          className="all-meals border border-[2px] border-[#F2F2F2B8] my-[28px] !px-[8px] !py-[25px] rounded-[10px] !z-[0]"
        >
          {recipes?.map((recipe, index) => (
            <SwiperSlide key={index}>
              <div>
                <h3 className="text-[14px] font-[700] font-[Lato] text-[#0575E6] mb-2">
                  {t(`days.${recipe?.title?.toLowerCase()}`)}
                </h3>
                <div className="!grid md:!grid-cols-2 lg:!grid-cols-1 xl:!grid-cols-5 gap-[20px] bg-white rounded-lg">
                  {/* Image source uses imageToShow state, which is managed by the effects/handlers */}
                  <ImageWithFallback
                    src={getImageUrl(imageToShow)}
                    alt={t(`days.${recipe?.title?.toLowerCase()}`)}
                    width={"100%"}
                    height={"128px"}
                    className="w-full !object-contain max-w-full md:max-w-[220px] lg:max-w-full xl:max-w-[220px] !max-h-[180px] md:!object-cover rounded-lg xl:col-span-2 xl:min-w-[170px] min-h-[180px]"
                  />
                  <div className="xl:col-span-3">
                    <ul className="">
                      {recipe?.description?.map((item, idx) => (
                        <li
                          className={`text-nowrap text-[14px] flex items-center justify-between mx-2 cursor-pointer ${
                            hoveredMeal === mealLabels[idx]
                              ? "text-[#0575E6] font-[600]"
                              : !isSwiping
                              ? "hover:text-[#0575E6] hover:font-[600]"
                              : ""
                          } ${idx === 0 && "!pt-0"} pt-2 lg:pt-3`}
                          key={idx}
                          onClick={() => handleOpenRecipe(mealLabels[idx])}
                          onMouseEnter={() => handleMouseEnter(mealLabels[idx])}
                          onMouseLeave={handleMouseLeave}
                        >
                          <p>
                            {t(
                              `dietPlanDashboard.allMeals.${mealLabels[
                                idx
                              ].toLowerCase()}`
                            )}
                          </p>
                          <p>{item}</p>
                        </li>
                      ))}
                      {recipe?.snacks?.afternoon_snacks?.Calories && (
                        <li
                          className={`text-nowrap text-[14px] flex items-center justify-between mx-2 pt-2 cursor-pointer ${
                            // Check if the current snack is the one being hovered
                            hoveredMeal === "afternoon_snacks"
                              ? "text-[#0575E6] font-[600]"
                              : // If not hovered, apply standard hover styles only when not swiping
                              !isSwiping
                              ? "hover:text-[#0575E6] hover:font-[600]"
                              : // Fallback
                                ""
                          }`}
                          onMouseEnter={() =>
                            handleSnackMouseEnter("afternoon_snacks")
                          }
                          onMouseLeave={handleSnackMouseLeave}
                        >
                          <p>
                            {t(`dietPlanDashboard.allMeals.afternoonSnack`)}
                          </p>
                          <p>
                            {recipe?.snacks.afternoon_snacks?.Calories} Kcal
                          </p>
                        </li>
                      )}
                      {recipe?.snacks?.evening_snacks?.Calories && (
                        <li
                          className={`text-nowrap text-[14px] flex items-center justify-between mx-2 pt-2 cursor-pointer  ${
                            // Check if the current snack is the one being hovered
                            hoveredMeal === "evening_snacks"
                              ? "text-[#0575E6] font-[600]"
                              : // If not hovered, apply standard hover styles only when not swiping
                              !isSwiping
                              ? "hover:text-[#0575E6] hover:font-[600]"
                              : // Fallback
                                ""
                          }`}
                          onMouseEnter={() =>
                            handleSnackMouseEnter("evening_snacks")
                          }
                          onMouseLeave={handleSnackMouseLeave}
                        >
                          <p>{t(`dietPlanDashboard.allMeals.eveningSnack`)}</p>
                          <p>{recipe?.snacks?.evening_snacks?.Calories} Kcal</p>
                        </li>
                      )}
                      <li className="text-nowrap text-[14px] text-[#0575E6] flex items-center justify-between mx-2 font-[600] pt-2">
                        <p>{t(`dietPlanDashboard.allMeals.total`)}</p>
                        <p>{recipe?.calories}</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Next Button */}
        <button className="swiper-button-next after:!text-[20px] after:!font-[600] absolute !-right-[13px] !top-[58%] p-[2px] !w-1 !h-1"></button>
        <button className="swiper-button-prev after:!text-[20px] after:!font-[600] absolute !-left-[13px] !top-[58%] md:!top-[58%] p-[2px] !w-1 !h-1"></button>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-2 md:gap-[24px] lg:gap-2 xl:gap-[24px] w-full">
        <button
          onClick={() => {
            setSelectedDay(currentRecipe?.title);
            handleViewDetails(currentRecipe?.title);
          }}
          className="p-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-[100px] text-nowrap text-[12px] md:text-[14px] lg:text-[12px] xl:text-[14px] h-[41px]"
        >
          {t(`dietPlanDashboard.allMeals.viewDetails`)}
        </button>
        <button
          onClick={handleDownloadDay}
          disabled={genratePlanAllowed || dayList}
          title={t(`dietPlanDashboard.allMeals.dailyGrocery`)}
          className="p-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] truncate text-white rounded-[100px] text-nowrap text-[12px] md:text-[14px] lg:text-[12px] xl:text-[14px] h-[41px]"
        >
          {dayList ? (
            <ButtonLoader />
          ) : (
            t(`dietPlanDashboard.allMeals.dailyGrocery`)
          )}
        </button>
        <button
          onClick={handleDownloadShoppingList}
          disabled={genratePlanAllowed || mondtlyList}
          title={t(`dietPlanDashboard.allMeals.weeklyGrocery`)}
          className="p-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] truncate text-white rounded-[100px] text-nowrap text-[12px] md:text-[14px] lg:text-[12px] xl:text-[14px] h-[41px]"
        >
          {mondtlyList ? (
            <ButtonLoader />
          ) : (
            t(`dietPlanDashboard.allMeals.weeklyGrocery`)
          )}
        </button>
      </div>
      <div className="flex justify-center mt-[15px] w-[100%]">
        <button
          onClick={() => dietPdfUrl && window.open(dietPdfUrl, "_blank")}
          disabled={!dietPdfUrl}
          className="p-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-[100px] text-nowrap text-[14px] lg:text-[12px] xl:text-[14px] min-w-[40%] h-[41px] flex items-center justify-center gap-2"
        >
          {dietPdfUrl ? (
            t(`dietPlanDashboard.allMeals.weeklyMealList`)
          ) : pdfGenerating ? (
            <>
              {t(`dietPlanDashboard.allMeals.generatingWeeklyMealList`)}
              <Loader2 className="animate-spin h-4 w-4" />
            </>
          ) : (
            t(`dietPlanDashboard.allMeals.weeklyMealList`)
          )}
        </button>
      </div>
      {showModal && (
        <WeeklyMealPlanner
          weeklyMeals={weeklyMeals}
          showModal={showModal}
          setShowModal={setShowModal}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          base_url={BASE_URL}
          handleSetDietPlan={handleSetDietPlan}
        />
      )}
    </>
  );
};

export default AllMeals;
